import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, StickyNote, UserCog, Send } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { tr, lbl } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { ChatPanel } from "../components/ChatPanel";

const When = ({ at }) => <span dir="ltr" className="text-muted-foreground text-xs">{(at || "").slice(0, 16).replace("T", " ")}</span>;

function Timeline({ events, notes, onResend }) {
  const items = [...events.map((e) => ({ ...e, k: "ev" })), ...notes.map((n) => ({ ...n, k: "nt" }))].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  return (
    <ol className="space-y-3" data-testid="lead-timeline">
      {items.map((e) => (
        <li key={e.id} className="border-s-2 border-[var(--divider)] ps-3" data-testid={`tl-${e.k}-${e.type || e.channel}`}>
          {e.k === "ev" ? (
            <><div className="text-sm"><span className="font-medium">{tr(`ld.ev_${e.type /* i18n-keys: created,enquiry,assigned,reassigned,opened,stage,note,call,branch,opt_out,test_drive,handover */}`)}</span> · {e.text}</div>
              <div><When at={e.at} /> <span className="text-xs text-muted-foreground">· {e.by_name}</span>{e.data?.outcome ? <span className="text-xs"> · {e.data.outcome}</span> : null}</div></>
          ) : (
            <><div className="text-sm"><span className="font-medium">{tr("ld.notification")}</span> · {e.event} · {e.channel} · <span className={e.status === "failed" ? "text-red-700" : ""}>{e.status}</span>{e.error ? ` (${e.error})` : ""}</div>
              <div className="flex items-center gap-2"><When at={e.at} />
                {e.status === "failed" && e.audience === "customer" && !e.resent && <button className="btn-ghost text-xs" onClick={() => onResend(e.id)} data-testid={`resend-${e.id}`}>{tr("ld.resend")}</button>}</div></>
          )}
        </li>
      ))}
    </ol>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [facets, setFacets] = useState(null);
  const [note, setNote] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [tdDate, setTdDate] = useState("");
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/leads/${id}`); setD(data); }
    catch (e) { if (e?.response?.status === 404) setD(false); else toast.error(formatApiError(e)); }
  }, [id]);
  useEffect(() => { load(); api.get("/leads/facets").then((r) => setFacets(r.data)).catch(() => {}); }, [load]);
  if (d === false) return <div className="py-10 text-center" data-testid="lead-not-found">{tr("ld.not_found")}</div>;
  if (!d) return <div className="py-10 text-center text-muted-foreground">{tr("x.loading")}</div>;
  const { lead, customer } = d;
  const act = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(formatApiError(e)); } };
  const addNote = (kind) => act(async () => { await api.post(`/leads/${id}/${kind}`, { text: note }); setNote(""); }, tr("ld.saved"));
  const fire = async (event) => {
    try {
      const { data } = await api.post("/crm/notify-customer", { event, lead_id: id, date: tdDate || null });
      const bad = (data.results || []).filter((r) => r.status !== "sent").map((r) => lbl(r.status));
      if (data.results?.length && !bad.length) toast.success(tr("ld.sent")); else toast.warning(tr("ld.not_sent", { p0: bad.join(", ") || "—" }));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="lead-detail-page" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="eyebrow"><Link to="/leads" className="hover:underline">{tr("ld.title")}</Link> · {lbl(lead.source)}</div>
          <h1 className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }} data-testid="lead-customer-name">{lead.customer_name}</h1>
          <div className="text-sm mt-1">{lead.vehicle_label ? <Link to={`/inventory/${lead.vehicle_id}`} className="underline" data-testid="lead-car">{lead.vehicle_label}</Link> : tr("ld.no_car")}</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select className="input h-10" value={lead.stage || "New"} onChange={(e) => act(() => api.patch(`/leads/${id}`, { stage: e.target.value }), tr("ld.saved"))} data-testid="lead-stage-select">
            {(facets?.stages || []).map((s) => <option key={s} value={s}>{lbl(s)}</option>)}
          </select>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <section className="space-y-4">
          <div className="border border-[var(--divider)] p-4 text-sm space-y-1" data-testid="lead-customer-card">
            <div><span className="text-muted-foreground">{tr("ld.l_whatsapp")}:</span> <span dir="ltr">{customer.whatsapp || customer.mobile || "—"}</span></div>
            <div><span className="text-muted-foreground">{tr("ld.l_email")}:</span> {customer.email || "—"}</div>
            <div><span className="text-muted-foreground">{tr("ld.l_preferred")}:</span> {lead.preferred_contact ? tr(`enq.pref_${lead.preferred_contact /* i18n-keys: whatsapp,call,email */}`) : "—"}</div>
            <div data-testid="lead-consent"><span className="text-muted-foreground">{tr("ld.consent")}:</span> {customer.consent?.given ? <>{tr("ld.yes")} <When at={customer.consent.at} /></> : tr("ld.no")}
              {customer.opted_out ? <span className="ms-2 text-amber-800">{tr("ld.opted_out")} <When at={customer.opted_out_at} /></span> : null}</div>
            {lead.referrer?.name && <div><span className="text-muted-foreground">{tr("ld.referrer")}:</span> {lead.referrer.name} <span dir="ltr">{lead.referrer.phone}</span></div>}
            <div><span className="text-muted-foreground">{tr("ld.col_salesperson")}:</span> <span data-testid="lead-assignee">{lead.assigned_name || tr("ld.unassigned")}</span></div>
            <Link to={`/customers/${customer.id}`} className="underline text-xs" data-testid="lead-customer-link">{tr("ld.open_customer")}</Link>
          </div>
          {facets?.can_assign && (
            <div className="flex gap-2" data-testid="lead-reassign">
              <select className="input h-10 flex-1" value={assignTo} onChange={(e) => setAssignTo(e.target.value)} data-testid="lead-assign-select">
                <option value="">{tr("ld.pick_person")}</option>
                {facets.staff.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
              <button className="btn-secondary" disabled={!assignTo} onClick={() => act(() => api.post(`/leads/${id}/assign`, { user_id: assignTo, reason: "Reassigned by manager" }), tr("ld.saved"))} data-testid="lead-assign-btn"><UserCog size={14} /> {tr("ld.reassign")}</button>
            </div>
          )}
          <div className="space-y-2">
            <textarea className="input w-full" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={tr("ld.note_ph")} data-testid="lead-note-input" />
            <div className="flex gap-2">
              <button className="btn-secondary" disabled={!note.trim()} onClick={() => addNote("notes")} data-testid="lead-add-note"><StickyNote size={14} /> {tr("ld.add_note")}</button>
              <button className="btn-secondary" disabled={!note.trim()} onClick={() => addNote("calls")} data-testid="lead-log-call"><Phone size={14} /> {tr("ld.log_call")}</button>
            </div>
          </div>
          <div className="border border-[var(--divider)] p-3 space-y-2 text-sm" data-testid="lead-notify-box">
            <div className="font-medium">{tr("ld.tell_customer")}</div>
            <div className="flex flex-wrap gap-2 items-center">
              <input type="datetime-local" className="input h-9" value={tdDate} onChange={(e) => setTdDate(e.target.value)} data-testid="lead-td-date" />
              <button className="btn-ghost" disabled={!tdDate} onClick={() => fire("test_drive_booked")} data-testid="notify-test-drive"><Send size={14} /> {tr("ld.n_test_drive")}</button>
              <button className="btn-ghost" onClick={() => fire("sale_completed")} data-testid="notify-sale-completed"><Send size={14} /> {tr("ld.n_registration")}</button>
              <button className="btn-ghost" onClick={() => fire("ready_for_delivery")} data-testid="notify-ready"><Send size={14} /> {tr("ld.n_ready")}</button>
            </div>
          </div>
          <Timeline events={d.events} notes={d.notifications} onResend={(lid) => act(() => api.post(`/crm/notifications/${lid}/resend`), tr("ld.sent"))} />
        </section>
        <section><ChatPanel lead={lead} isAdmin={user?.role === "admin"} /></section>
      </div>
    </div>
  );
}
